import { LoadingStatus, Skeleton, SkeletonBadge, SkeletonPills, SkeletonText } from "./Skeleton";

export type DataTableSkeletonCell = "multiline" | "badge" | "pills" | "text" | "short";

interface DataTableSkeletonProps {
  headers: string[];
  rows?: number;
  cellVariants: DataTableSkeletonCell[];
  loadingLabel?: string;
}

function SkeletonCell({ variant }: { variant: DataTableSkeletonCell }) {
  switch (variant) {
    case "multiline":
      return (
        <div className="space-y-2">
          <SkeletonText width="w-32" />
          <SkeletonText width="w-44" className="h-3" />
        </div>
      );
    case "badge":
      return (
        <div className="space-y-2">
          <SkeletonBadge />
          <SkeletonText width="w-24" className="h-3" />
        </div>
      );
    case "pills":
      return <SkeletonPills />;
    case "short":
      return <SkeletonText width="w-8" />;
    case "text":
    default:
      return <SkeletonText width="w-28" />;
  }
}

/** Table-shaped skeleton with real column headers to prevent layout shift. */
export function DataTableSkeleton({
  headers,
  rows = 8,
  cellVariants,
  loadingLabel = "Loading table data",
}: DataTableSkeletonProps) {
  return (
    <div
      className="overflow-auto rounded-[var(--card-radius)] border border-border-default bg-surface-raised p-4 shadow-card"
      role="status"
      aria-busy="true"
    >
      <LoadingStatus label={loadingLabel} />
      <table className="w-full text-sm">
        <thead className="text-content-secondary">
          <tr>
            {headers.map((header) => (
              <th key={header} className="text-left py-2 pr-3 font-medium">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <tr key={rowIndex} className="border-t border-border-subtle align-top">
              {cellVariants.map((variant, columnIndex) => (
                <td key={columnIndex} className="py-3 pr-3">
                  <SkeletonCell variant={variant} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-4 flex items-center justify-between">
        <Skeleton className="h-3 w-24" />
        <div className="flex gap-2">
          <Skeleton className="h-7 w-12 rounded" />
          <Skeleton className="h-7 w-12 rounded" />
        </div>
      </div>
    </div>
  );
}
